/**
 * Search estimate hook (Block S2).
 *
 * Calls POST /api/v1/search/estimate to fetch the upper-bound credits range
 * before user clicks "Start". Pure preview — no balance hold, no Celery.
 *
 * Triggered manually (button click) — not auto-fetched on every render,
 * because the request is debounced via TanStack Query keys.
 */
import { useQuery } from '@tanstack/react-query';

import api from '@/lib/api';
import type { SearchTierKey } from '@/store/searchStore';

export interface SearchEstimate {
  currency: string;
  tier_key: SearchTierKey;
  row_count_total: number;
  row_count_effective: number;
  is_test_run: boolean;
  test_run_limit?: number;
  max_queries_per_row: number;
  max_pages_per_row: number;
  max_candidates_per_query: number;
  max_queries_total: number;
  max_pages_total: number;
  max_extracts_total: number;
  estimate_min_credits: string; // Decimal as string
  estimate_max_credits: string;
  per_row_min_credits: string;
  per_row_max_credits: string;
  breakdown: {
    per_row_queries_max: number;
    per_row_pages_max: number;
    per_row_candidates_max: number;
    unit_credit_per_query: string;
    unit_credit_per_page_fetch: string;
    unit_credit_per_extract: string;
    priority_domains_count: number;
    from_admin: boolean;
    notes: string;
  };
  warning: string | null;
}

export interface SearchEstimateRequest {
  dataset_id: string;
  tier_key: SearchTierKey;
  is_test_run: boolean;
  test_run_limit?: number;
  priority_domains?: string[];
}

export function useSearchEstimate(
  body: SearchEstimateRequest | null,
  enabled: boolean,
) {
  return useQuery<SearchEstimate>({
    queryKey: ['search-estimate', body],
    queryFn: async () => {
      if (!body) throw new Error('No body');
      const { data } = await api.post<SearchEstimate>(
        '/search/estimate',
        body,
      );
      return data;
    },
    enabled: enabled && !!body?.dataset_id,
    staleTime: 30_000,
    retry: false,
  });
}
